import Vue from 'vue'
import { i18n } from '@/internationalization/i18nSetup'
import {
  CommonaddressAddress,
  LanguagePreferredLanguage,
  TypeDate,
  V2EmploymentDuration,
  V2EmploymentType
} from '@/types/api'
import { DigitalReferenceFormResponse } from '@/types/models'

const incomeLabelKeys: Record<number, string> = {
  0: 'message.applicationForm.noIncome',
  44999: 'message.applicationForm.upTo45000',
  45000: 'message.applicationForm.45000to55000',
  55000: 'message.applicationForm.55000to70000',
  70000: 'message.applicationForm.70000to85000',
  85000: 'message.applicationForm.85000to100000',
  100000: 'message.applicationForm.moreThan100000'
}

const employmentDurationLabelKeys: Record<string, string> = {
  [V2EmploymentDuration.EMPLOYMENTDURATIONLESSTHAN3MONTHS]: 'message.applicationForm.less3Month',
  [V2EmploymentDuration.EMPLOYMENTDURATIONMORETHAN3MONTHS]: 'message.applicationForm.more3Month',
  [V2EmploymentDuration.EMPLOYMENTDURATION1YEAR]: 'message.applicationForm.since1Year',
  [V2EmploymentDuration.EMPLOYMENTDURATION2YEARS]: 'message.applicationForm.since2Years',
  [V2EmploymentDuration.EMPLOYMENTDURATION3YEARS]: 'message.applicationForm.since3Years',
  [V2EmploymentDuration.EMPLOYMENTDURATION4YEARS]: 'message.applicationForm.since4Years',
  [V2EmploymentDuration.EMPLOYMENTDURATION5YEARS]: 'message.applicationForm.since5Years',
  [V2EmploymentDuration.EMPLOYMENTDURATIONMORETHAN5YEARS]: 'message.applicationForm.moreThan5Years'
}

const employmentTypeLabelKeys: Record<string, string> = {
  [V2EmploymentType.EMPLOYMENT_TYPE_INDEFINITE]: 'message.applicationForm.indefinite',
  [V2EmploymentType.EMPLOYMENT_TYPE_LIMITED]: 'message.applicationForm.limited',
  [V2EmploymentType.EMPLOYMENT_TYPE_TEMPORARY]: 'message.applicationForm.temporary'
}

const localeByApiLanguage: Record<string, string> = {
  [LanguagePreferredLanguage.PREFERRED_LANGUAGE_GERMAN]: 'de',
  [LanguagePreferredLanguage.PREFERRED_LANGUAGE_ENGLISH]: 'en',
  [LanguagePreferredLanguage.PREFERRED_LANGUAGE_ITALIAN]: 'it',
  [LanguagePreferredLanguage.PREFERRED_LANGUAGE_FRENCH]: 'fr'
}

const countryLabelKeys: Record<string, string> = {
  CH: 'message.generic.countries.switzerland',
  CHE: 'message.generic.countries.switzerland',
  DE: 'message.generic.countries.germany',
  DEU: 'message.generic.countries.germany',
  AT: 'message.generic.countries.austria',
  AUT: 'message.generic.countries.austria',
  LI: 'message.generic.countries.liechtenstein',
  LIE: 'message.generic.countries.liechtenstein'
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

function translate(key: string | undefined): string {
  return key ? String(i18n.t(key)) : ''
}

/**
 * The prefill fields arrive as plain strings so the backend can pass through whatever
 * the applicant selected. Translate known enum values and fall back to the raw text.
 */
export function formatEmploymentDuration(value?: string | null): string {
  if (!value || value === V2EmploymentDuration.UNDEFINED_EMPLOYMENT_DURATION) {
    return ''
  }
  return translate(employmentDurationLabelKeys[value]) || value
}

export function formatEmploymentType(value?: string | null): string {
  if (!value || value === V2EmploymentType.UNDEFINED_EMPLOYMENT_TYPE) {
    return ''
  }
  return translate(employmentTypeLabelKeys[value]) || value
}

export function formatIncome(value?: string | null): string {
  if (!value || !value.trim()) {
    return ''
  }
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return value
  }
  return translate(incomeLabelKeys[numeric]) || Vue.filter('formatPrice')(numeric, 'CHF', true)
}

export function formatTypeDate(value?: TypeDate | null): string {
  if (!value) {
    return ''
  }
  const formatted = Vue.filter('objectToDate')(value)
  return formatted === '-' ? '' : formatted
}

export function formatRentedSince(value?: string | null): string {
  if (!value || !value.trim()) {
    return ''
  }
  const match = ISO_DATE.exec(value.trim())
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

export function formatPreviousGrossRent(value?: number | null): string {
  if (value === null || value === undefined || value <= 0) {
    return ''
  }
  return Vue.filter('formatPrice')(value, 'CHF', true)
}

function formatCountry(country?: string | null): string {
  if (!country) {
    return ''
  }
  return translate(countryLabelKeys[country]) || country
}

/**
 * Propbase stores contact names as "lastName firstName". The public form must show first name first.
 */
export function formatPersonName(value?: string | null): string {
  if (!value || !value.trim()) {
    return ''
  }
  const parts = value.trim().split(/\s+/)
  if (parts.length < 2) {
    return value.trim()
  }
  return `${parts.slice(1).join(' ')} ${parts[0]}`
}

export function formatAddress(address?: CommonaddressAddress | null): string {
  if (!address) {
    return ''
  }
  const street = [address.street, address.streetNumber].filter(Boolean).join(' ')
  const city = [address.zip, address.city].filter(Boolean).join(' ')
  return [address.careOf, street, city, formatCountry(address.country)].filter(Boolean).join(', ')
}

const LOGO_KEYS = ['logoUrl', 'accountLogoUrl', 'logo_url', 'account_logo_url', 'logo']
const ACCOUNT_ID_KEYS = [
  'accountId',
  'marketingAccountId',
  'ownerId',
  'account_id',
  'marketing_account_id',
  'owner_id'
]
const OBJECT_ID_KEYS = ['objectId', 'object_id', 'marketingObjectId', 'marketing_object_id']

function asNonEmptyId(value: string): string {
  return value && value !== '-1' ? value : ''
}

function extraField(source: unknown, key: string): string {
  if (!source || typeof source !== 'object') {
    return ''
  }
  const value = (source as Record<string, unknown>)[key]
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim()
  }
  return ''
}

function nestedUrl(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return asNonEmptyId(String(value).trim())
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ''
  }
  const record = value as Record<string, unknown>
  return nestedUrl(record.url) || nestedUrl(record.logoUrl) || nestedUrl(record.accountLogoUrl)
}

function pickFromSource(source: unknown, keys: string[], asUrl: boolean): string {
  if (!source || typeof source !== 'object') {
    return ''
  }
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const found = asUrl ? nestedUrl(record[key]) : asNonEmptyId(extraField(source, key))
    if (found) {
      return found
    }
  }
  for (const child of Object.values(record)) {
    if (!child || typeof child !== 'object' || Array.isArray(child)) {
      continue
    }
    const childRecord = child as Record<string, unknown>
    for (const key of keys) {
      const found = asUrl ? nestedUrl(childRecord[key]) : asNonEmptyId(extraField(child, key))
      if (found) {
        return found
      }
    }
  }
  return ''
}

export function getReferenceFormLogoUrl(form?: DigitalReferenceFormResponse | null): string {
  if (!form) {
    return ''
  }
  return pickFromSource(form, LOGO_KEYS, true) || pickFromSource(form.administration, LOGO_KEYS, true)
}

export function getReferenceFormAccountId(form?: DigitalReferenceFormResponse | null): string {
  if (!form) {
    return ''
  }
  return pickFromSource(form, ACCOUNT_ID_KEYS, false) || pickFromSource(form.administration, ACCOUNT_ID_KEYS, false)
}

export function getReferenceFormObjectId(form?: DigitalReferenceFormResponse | null): string {
  if (!form) {
    return ''
  }
  return pickFromSource(form, OBJECT_ID_KEYS, false) || pickFromSource(form.administration, OBJECT_ID_KEYS, false)
}

export function localeForApiLanguage(language?: LanguagePreferredLanguage | null): string | null {
  return (language && localeByApiLanguage[language]) || null
}
